import {
  ChangeDetectionStrategy,
  computed,
  Component,
  input,
  output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { Tooltip } from 'primeng/tooltip';
import { ParameterSchema } from '../../../models/pricing-model.model';
import {
  buildEditorFieldsFromSchemas,
  type ParameterEditorField,
} from './parameter-editor.utils';

@Component({
  selector: 'app-parameter-editor',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    InputNumberModule,
    InputTextModule,
    CheckboxModule,
    Select,
    Tooltip,
  ],
  templateUrl: './parameter-editor.component.html',
})
export class ParameterEditorComponent {
  readonly parameterSchemas = input<Record<string, ParameterSchema>>({});
  readonly values = input<Record<string, string | null>>({});
  // Namespaces the generated control ids so multiple editors can render on the
  // same page (e.g. one per CAD object) without producing duplicate DOM ids.
  readonly idPrefix = input<string>('');

  readonly parameterChanged = output<{
    key: string;
    value: string | null;
  }>();

  readonly items = computed<ParameterEditorField[]>(() =>
    buildEditorFieldsFromSchemas(this.parameterSchemas()),
  );
  readonly maxFractionDigits = 4;

  onDecimalChange(key: string, value: number | null): void {
    this.parameterChanged.emit({
      key,
      value: value == null ? null : String(value),
    });
  }

  onStringChange(key: string, value: string | null): void {
    this.parameterChanged.emit({ key, value: value || null });
  }

  onBooleanChange(key: string, value: boolean): void {
    this.parameterChanged.emit({ key, value: value ? 'true' : 'false' });
  }

  getDecimalValue(key: string): number | null {
    const rawValue = this.values()[key];
    if (rawValue == null || rawValue === '') {
      return null;
    }

    const numericValue = Number(rawValue);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  getStringValue(key: string): string | null {
    return this.values()[key] ?? null;
  }

  getBooleanValue(key: string): boolean {
    return this.values()[key] === 'true';
  }

  getControlId(key: string): string {
    return `${this.idPrefix()}param-${key}`;
  }
}
